import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import "../MesrequetesStyles/DemandeConges.css";
import {
    Calendar,
    Clock,
    Plus,
    FileText,
    AlertCircle,
    CheckCircle,
    XCircle,
    Upload,
    X,
    Eye,
    Trash2,
    FileText as FileTextIcon,
    TrendingUp,
    Award,
    RefreshCw,
    ShieldCheck,
    ChevronLeft,
    ChevronRight,
} from "lucide-react";


// ✅ Leave requests (mine + create)
import {
    getMyLeaveRequestsRequest,
    createLeaveRequestRequest,
    clearLeaveRequestMessage,
    postponeLeaveRequestRequest,
} from "../../../../redux/actions/leaveRequestsActions";

import { getWorkdaysConfigRequest } from "../../../../redux/actions/workDaysActions";

// ✅ Param congés (typesAbsences + justificatifs)
import { fetchParamCongesRequest } from "../../../../redux/actions/paramCongesActions";

// ✅ Balance (soldes)
import {
    getUserSoldesRequest,
    checkBalanceRequest,
    cancelLeaveRequestRequest,
    getUserMovementsRequest,
    getAllMovementsRequest,
} from "../../../../redux/actions/leaveBalanceActions";

import { API_BASE_URL } from "../../../../../api";
import { setAuthenticatedUser } from "../../../../redux/actions/authActions";



const DemandeConges = () => {
    const dispatch = useDispatch();

    // ===================== REDUX: leave requests =====================
    const leaveState = useSelector((state) => state.leaveRequests || state.leaveRequest || {});
    const myRequests = leaveState.myRequests || [];
    const loading = !!leaveState.loading;
    const apiError = leaveState.error || "";
    const apiMessage = leaveState.message || "";

    // Extraction des jours de travail configurés
    const { workdaysConfig } = useSelector((state) => state.workDays || {});

    // ===================== REDUX: param congés =====================
    const paramState = useSelector((state) => state.paramConges || {});
    const typesAbsences = paramState.typesAbsences || [];

    const paramLoading = !!paramState.loading;

    // ===================== REDUX: balance (soldes) =====================
    const balanceState = useSelector((state) => state.leaveBalance || {});
    const userSoldes = balanceState.userSoldes || [];
    const movements = balanceState.movements || [];
    const balanceLoading = !!balanceState.loading;
    const balanceCheck = balanceState.balanceCheck || null;

    // ===================== REDUX: user info =====================
    const authState = useSelector((state) => state.auth || {});
    const currentUser = authState.user || authState.currentUser || {};
    const userId = currentUser.id || sessionStorage.getItem("uid");
    // Après un rechargement, l'entreprise sélectionnée est stockée sous
    // `selectedCompanyId`. Sans ce repli, le chargement des soldes est ignoré
    // et l'interface affiche 0 alors qu'un solde existe en base.
    const companyId = currentUser.companyId || sessionStorage.getItem("selectedCompanyId") || sessionStorage.getItem("companyId");

    // ===================== UI STATE =====================
    const [activeTab, setActiveTab] = useState("solde");
    const [showDemandeModal, setShowDemandeModal] = useState(false);
    const [showJustificatifModal, setShowJustificatifModal] = useState(false);
    const [showAnnulationModal, setShowAnnulationModal] = useState(false);
    const [showReportModal, setShowReportModal] = useState(false);
    const [selectedDemande, setSelectedDemande] = useState(null);
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [soldeError, setSoldeError] = useState("");

    // ===================== COLLABORATEUR (TEMP) =====================
    const collaborateur = {
        matricule: currentUser.employeeCode || "EMP004",
        nom: currentUser.fullName || "Utilisateur",
        poste: currentUser.position || "",
        departement: currentUser.department || "",
    };

    // ===================== JOURS FÉRIÉS FIXES =====================
    const [fixedHolidays] = useState([
        { date: "01-01", description: "Nouvel an", selected: true },
        { date: "01-11", description: "Manifeste de l'Indépendance", selected: true },
        { date: "01-14", description: "Nouvel An Amazigh", selected: true },
        { date: "05-01", description: "Fête du Travail", selected: true },
        { date: "07-30", description: "Fête du Trône", selected: true },
        { date: "08-14", description: "Commémoration de l'Oued Ed-Dahab", selected: true },
        { date: "08-20", description: "Révolution du Roi et du Peuple", selected: true },
        { date: "08-21", description: "Fête de la Jeunesse", selected: true },
        { date: "11-06", description: "Anniversaire de la Marche Verte", selected: true },
        { date: "11-18", description: "Fête de l'Indépendance", selected: true },
    ]);

    // ===================== ✅ JOURS FÉRIÉS HIJRI (NOUVEAUX) =====================
    const [hijriHolidays] = useState([
        {
            name: "Aïd al-Fitr",
            date: "2026-03-20", // Date grégorienne estimée pour 2026
            duration: 2,
            selected: true
        },
        {
            name: "Aïd al-Adha",
            date: "2026-05-27", // Date grégorienne estimée pour 2026
            duration: 2,
            selected: true
        },
        {
            name: "Nouvel An Hijri",
            date: "2026-07-07",
            duration: 1,
            selected: true
        },
        {
            name: "Mawlid Nabawi",
            date: "2026-09-15",
            duration: 1,
            selected: true
        },
    ]);

    // ===================== JOURS DE TRAVAIL =====================
    const [workDays] = useState({
        lundi: { isWorking: true, hours: 8 },
        mardi: { isWorking: true, hours: 8 },
        mercredi: { isWorking: true, hours: 8 },
        jeudi: { isWorking: true, hours: 8 },
        vendredi: { isWorking: true, hours: 8 },
        samedi: { isWorking: false, hours: 0 },
        dimanche: { isWorking: false, hours: 0 },
    });

    const WEEKDAYS = ["lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi", "dimanche"];
    const WEEKDAYS_SHORT = ["L", "M", "M", "J", "V", "S", "D"];

    // ===================== ✅ SOLDES TRANSFORMÉS ET TRIÉS =====================
    const soldesConges = useMemo(() => {
        const transformed = (userSoldes || []).map((solde) => {
            const soldeInitial = Number(solde.soldeInitial || 0);
            const soldeAcquis = Number(solde.soldeAcquis || 0);
            const soldeConsomme = Number(solde.soldeConsomme || 0);
            const soldeRestant = Number(solde.soldeRestant || 0);

            return {
                id: solde.id,
                type: solde.absenceTypeNom || solde.absenceTypeName || "Type inconnu",
                code: solde.absenceTypeCode || "",
                couleur: solde.absenceTypeCouleur || "#3b82f6",
                soldeTotal: soldeInitial + soldeAcquis,
                initial: soldeInitial,
                acquis: soldeAcquis,
                pris: soldeConsomme,
                enAttente: 0,
                restant: soldeRestant,
                periodeReference: `${solde.annee || new Date().getFullYear()}`,
                reportPossible: 0,
                dateExpiration: solde.dateExpiration || null,
            };
        });

        return transformed.sort((a, b) => b.restant - a.restant);
    }, [userSoldes]);

    const soldePrincipal = useMemo(() => {
        if (!soldesConges || soldesConges.length === 0) return null;
        const annualLeave = soldesConges.find(
            (s) => s.type && s.type.toLowerCase().includes("annuel")
        );
        return annualLeave || soldesConges[0];
    }, [soldesConges]);

    // ===================== FORMULAIRE =====================
    const [formData, setFormData] = useState({
        absenceTypeId: "",
        dateDebut: "",
        dateFin: "",
        commentaire: "",
        justificatif: null,
        autorisation: null,
        demiJourneeDebut: false,
        demiJourneeDebutType: "MATIN",
        demiJourneeFin: false,
        demiJourneeFinType: "MATIN",
    });

    const [justificatifUpload, setJustificatifUpload] = useState({
        demandeId: null,
        fichier: null,
    });

    // ===================== INIT LOAD =====================
    useEffect(() => {
        dispatch(getMyLeaveRequestsRequest());

        if (!typesAbsences || typesAbsences.length === 0) {
            dispatch(fetchParamCongesRequest());
        }
        dispatch(getWorkdaysConfigRequest());

        const isValidUser = userId && userId !== "0" && userId !== 0;
        const isValidCompany = companyId && companyId !== "0" && companyId !== 0;

        if (isValidUser && isValidCompany) {
            dispatch(getUserSoldesRequest({ userId, companyId }));
        }

        if (isValidUser) {
            // Utilise la route utilisateur (qui renvoie maintenant tout le contenu via le backend)
            dispatch(getUserMovementsRequest(userId));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (apiMessage || apiError) dispatch(clearLeaveRequestMessage());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    useEffect(() => {
        if (apiMessage) {
            Swal.fire({
                icon: "success",
                title: "Opération réussie",
                text: apiMessage,
                timer: 3000,
                showConfirmButton: false,
            });
            if (apiMessage.includes("succès") || apiMessage.includes("reportée")) {
                const isValidUser = userId && userId !== "0" && userId !== 0;
                const isValidCompany = companyId && companyId !== "0" && companyId !== 0;
                if (isValidUser && isValidCompany) {
                    dispatch(getUserSoldesRequest({ userId, companyId }));
                    dispatch(getMyLeaveRequestsRequest());
                }
            }
            dispatch(clearLeaveRequestMessage());
        }
        if (apiError) {
            Swal.fire({
                icon: "error",
                title: "Erreur",
                text: typeof apiError === "string" ? apiError : (apiError.message || "Une erreur est survenue"),
            });
            dispatch(clearLeaveRequestMessage());
        }
    }, [apiMessage, apiError, dispatch, userId, companyId]);


    const [isDelegationEnabled, setIsDelegationEnabled] = useState(currentUser?.isLeaveDelegationEnabled || false);
    useEffect(() => {
        setIsDelegationEnabled(currentUser?.isLeaveDelegationEnabled || false);
    }, [currentUser]);


    const handleToggleDelegation = async (enabled) => {
        try {
            const token = sessionStorage.getItem("accessToken");
            const tenantId = sessionStorage.getItem("selectedCompanyId");
            
            const response = await fetch(`${API_BASE_URL}/api/users/${userId}/delegation?enabled=${enabled}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "uid": String(userId),
                    "X-Tenant-Id": tenantId || "",
                    "Authorization": token ? `Bearer ${token}` : ""
                }
            });
            if (response.ok) {
                const updatedUser = await response.json();
                setIsDelegationEnabled(enabled);
                
                // ✅ Mise à jour du state Redux pour que ça persiste à travers la navigation
                dispatch(setAuthenticatedUser(updatedUser));
                
                Swal.fire({
                    title: "Succès",
                    text: `La délégation est désormais ${enabled ? "activée" : "désactivée"}.`,
                    icon: "success",
                    timer: 1500,
                    showConfirmButton: false,
                    toast: true,
                    position: 'top-end'
                });

            } else {
                throw new Error("Erreur lors de la mise à jour");
            }
        } catch (error) {
            console.error("Error toggling delegation:", error);
            Swal.fire("Erreur", "Impossible de mettre à jour la délégation.", "error");
            // Revert switch
            setIsDelegationEnabled(!enabled);
        }
    };


    // ===================== HELPERS =====================
    const safeStr = (v) => (v === null || v === undefined ? "" : String(v));

    const getAbsenceTypeLabel = (t) =>
        t?.label ||
        t?.libelle ||
        t?.name ||
        t?.nom ||
        t?.type ||
        "Type";

    const getAbsenceTypeColor = (t) =>
        t?.couleur ||
        t?.color ||
        t?.hexColor ||
        t?.backgroundColor ||
        "#3b82f6";

    const checkIsHoliday = (d) => {
        if (!d) return false;
        const month = (d.getMonth() + 1).toString().padStart(2, "0");
        const day = d.getDate().toString().padStart(2, "0");
        const dateStr = `${month}-${day}`;
        const isFixed = (fixedHolidays || []).some((h) => h.date === dateStr && h.selected);
        if (isFixed) return true;

        const isHijri = (hijriHolidays || []).some((h) => {
            if (!h.selected || !h.date) return false;
            const holidayDate = new Date(h.date);
            const checkDate = new Date(d);
            holidayDate.setHours(0, 0, 0, 0);
            checkDate.setHours(0, 0, 0, 0);

            for (let i = 0; i < (h.duration || 1); i++) {
                const testDate = new Date(holidayDate);
                testDate.setDate(testDate.getDate() + i);
                testDate.setHours(0, 0, 0, 0);

                if (testDate.getTime() === checkDate.getTime()) return true;
            }
            return false;
        });
        return isHijri;
    };

    const calculerDuree = (debut, fin, checkDemiDebut = formData.demiJourneeDebut, checkDemiFin = formData.demiJourneeFin) => {
        if (!debut || !fin) return 0;
        const start = new Date(debut);
        const end = new Date(fin);
        let days = 0;
        
        const isSingleDay = debut === fin;

        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
            const dayIndex = d.getDay();
            const dayName = dayIndex === 0 ? "dimanche" : 
                            dayIndex === 1 ? "lundi" : 
                            dayIndex === 2 ? "mardi" : 
                            dayIndex === 3 ? "mercredi" : 
                            dayIndex === 4 ? "jeudi" : 
                            dayIndex === 5 ? "vendredi" : "samedi";
            
            let isWorking = true;
            if (workdaysConfig && workdaysConfig.workDays && workdaysConfig.workDays[dayName]) {
                const cfg = workdaysConfig.workDays[dayName];
                isWorking = typeof cfg.isWorking === "boolean" ? cfg.isWorking : 
                            typeof cfg.working === "boolean" ? cfg.working : true;
            } else if (workDays && workDays[dayName]) {
                isWorking = workDays[dayName].isWorking;
            }
            if (isWorking) {
                if (!checkIsHoliday(d)) {
                    const y = d.getFullYear();
                    const month = String(d.getMonth() + 1).padStart(2, "0");
                    const dayVal = String(d.getDate()).padStart(2, "0");
                    const dStr = `${y}-${month}-${dayVal}`;

                    if (isSingleDay) {
                        if (checkDemiDebut || checkDemiFin) {
                            days += 0.5;
                        } else {
                            days += 1.0;
                        }
                    } else {
                        if (dStr === debut && checkDemiDebut) {
                            days += 0.5;
                        } else if (dStr === fin && checkDemiFin) {
                            days += 0.5;
                        } else {
                            days += 1.0;
                        }
                    }
                }
            }
        }
        return days;
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "";
        const date = new Date(dateStr);
        if (Number.isNaN(date.getTime())) return dateStr;
        return date.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
    };

    const formatMonth = (date) => date.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });

    // ===================== TYPES DYNAMIQUES (avec règles) =====================
    const typesCongesDisponibles = useMemo(() => {
        return (typesAbsences || []).map((t) => {
            const id = t?.id;
            const label = getAbsenceTypeLabel(t);
            const couleur = getAbsenceTypeColor(t);

            return {
                id,
                value: label,
                label,
                couleur,
                requireJustificatif: !!t?.justificationRequired,
                justificatifMandatory: !!t?.justificationMandatory,
                justificationType: t?.justificationType || "Justificatif",
                requireAutorisation: !!t?.requireAutorisation || !!t?.autorisationObligatoire || !!t?.isAutorisationRequired,
                maxDaysPerRequest: t?.maxDaysPerRequest,
                periodeDe: t?.periodeDe,
                periodeA: t?.periodeA,
                negativeBalanceAllowed: !!t?.negativeBalanceAllowed,
                maxNegativeBalance: t?.maxNegativeBalance,
            };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [typesAbsences]);

    const selectedTypeObj = useMemo(() => {
        if (!formData.absenceTypeId) return null;
        return typesCongesDisponibles.find((t) => String(t.id) === String(formData.absenceTypeId)) || null;
    }, [formData.absenceTypeId, typesCongesDisponibles]);

    const selectedTypeLabel = selectedTypeObj?.label || "";

    // ===================== MAP API -> UI HISTORIQUE =====================
    const historiqueDemandes = useMemo(() => {
        return (myRequests || []).map((d) => {
            const deb = d.dateDebut || d.startDate || d.fromDate;
            const fin = d.dateFin || d.endDate || d.toDate;

            const duree =
                Number.isFinite(d.duree) ? d.duree :
                    (deb && fin ? calculerDuree(deb, fin) : 0);

            const createdAt =
                d.createdAt ||
                d.dateDemande ||
                d.requestDate ||
                d.datesDemande ||
                "";

            const typeNom =
                d.absenceTypeNom ||
                d.absenceTypeName ||
                d.absenceTypeLabel ||
                d.typeConge ||
                "Congé";

            const typeMatch = (typesCongesDisponibles || []).find((t) => t.label === typeNom);

            const statusRaw = safeStr(d.status || d.statut).toUpperCase();
            const statut =
                statusRaw === "PENDING" || statusRaw === "PENDING_MANAGER" || statusRaw === "PENDING_ADMIN" || statusRaw === "EN_ATTENTE" ? "enAttente" :
                    statusRaw === "APPROVED" || statusRaw === "APPROUVE" ? "approuve" :
                        statusRaw === "REJECTED" || statusRaw === "REFUSE" ? "refuse" :
                            statusRaw === "CANCELLED" || statusRaw === "ANNULE" ? "annule" :
                                statusRaw === "POSTPONED" || statusRaw === "REPORTE" ? "reporte" :
                                    statusRaw === "POSTPONEMENT_REQUEST" ? "reporte_en_attente" :
                                        (d.status || d.statut || "enAttente");


            const motifRejet = d.motifRejet || d.rejectionReason || d.managerComment || d.adminComment || "";

            return {
                id: d.id,
                typeConge: typeNom,
                couleurType: typeMatch?.couleur || "#3b82f6",
                dateDebut: deb,
                dateFin: fin,
                duree,
                datesDemande: createdAt ? String(createdAt).slice(0, 10) : "",
                commentaire: d.commentaire || d.comment || "",
                statut,
                justificatifFourni: !!d.justificatifFourni,
                justificatifNom: d.justificatifNom || d.justificatifName || null,
                autorisationFournie: !!d.autorisationFournie,
                autorisationNom: d.autorisationNom || d.autorisationName || null,
                motifRejet,
                soldeInitial: d.soldeInitial,
                soldeConsomme: d.soldeConsomme,
                soldeRestant: d.soldeRestant,
                totalGlobal: d.totalRestant,
                originalDateDebut: d.originalDateDebut,
                originalDateFin: d.originalDateFin,
                employeeName: d.employeeName || "",
                employeeCode: d.employeeCode || "",
                demandeurName: d.demandeurName || "",
                demandeurCode: d.demandeurCode || "",
            };


        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [myRequests, typesCongesDisponibles]);

    // ===================== ✅ APPROVED REQUESTS & TOTAL BALANCE =====================
    const totalRestant = useMemo(() => {
        return soldesConges.reduce((acc, s) => acc + (Number(s.restant) || 0), 0);
    }, [soldesConges]);

    const approvedRequests = useMemo(() => {
        return historiqueDemandes.filter((d) => d.statut === "approuve" || d.statut === "annule" || d.statut === "reporte");
    }, [historiqueDemandes]);

    const combinedHistory = useMemo(() => {
        // 1. Filtrer les demandes : uniquement approuvées, annulées, reportées
        const filteredRequests = (historiqueDemandes || []).filter((req) =>
            req.statut === "approuve" || req.statut === "annule" || req.statut === "reporte"
        );
        // Convertir en structure de mouvements (débits)
        const requestsAsMovements = filteredRequests.map((req) => {
            const dateStr = `Du ${formatDate(req.dateDebut)} au ${formatDate(req.dateFin)}`;
            const motifText = req.commentaire ? `${dateStr} - ${req.commentaire}` : dateStr;
            
            // Pour les demandes, la valeur de débit est négative
            const valeur = -Number(req.duree || 0);

            const refText = req.demandeurName 
                ? `${req.demandeurName} (${req.demandeurCode || "—"})`
                : `REF-${req.id}`;

            return {
                id: `req-${req.id}`,
                employeeName: req.employeeName,
                employeeCode: req.employeeCode,
                dateMouvement: req.createdAt || req.dateDebut,
                reference: refText,
                motif: motifText,
                commentaire: req.motifRejet ? `Motif rejet/annulation: ${req.motifRejet}` : "",
                absenceTypeNom: req.typeConge,
                absenceTypeCouleur: req.couleurType,
                soldeInitialSnapshot: req.soldeInitial,
                soldeRestantSnapshot: req.soldeRestant,
                valeur: valeur,
                isLeaveRequest: true,
                statut: req.statut,
            };
        });

        // 2. Filtrer les mouvements pour ne garder que ceux non liés à une demande (exclure CONSUMPTION)
        const positiveMovements = (movements || [])
            .filter((m) => m.typeMouvement !== "CONSUMPTION")
            .map((m) => ({
                id: `mov-${m.id}`,
                employeeName: m.employeeName,
                employeeCode: m.employeeCode,
                dateMouvement: m.dateMouvement,
                reference: m.crediteurName ? `${m.crediteurName} (${m.crediteurMatricule || "—"})` : "SYS_AUTO",
                motif: m.commentaire || m.motif || "Ajustement",
                commentaire: "",
                absenceTypeNom: m.absenceTypeNom,
                absenceTypeCouleur: m.absenceTypeCouleur,
                soldeInitialSnapshot: m.soldeInitialSnapshot,
                soldeRestantSnapshot: m.soldeRestantSnapshot,
                valeur: Number(m.valeur || 0),
                isLeaveRequest: false,
                statut: "valide",
            }));

        // 3. Fusionner les deux listes
        const merged = [...requestsAsMovements, ...positiveMovements];

        // 4. Trier par date décroissante
        return merged.sort((a, b) => {
            const dateA = a.dateMouvement ? new Date(a.dateMouvement).getTime() : 0;
            const dateB = b.dateMouvement ? new Date(b.dateMouvement).getTime() : 0;
            return dateB - dateA;
        });
    }, [historiqueDemandes, movements]);

    // ===================== ✅ CALENDAR HELPERS AMÉLIORÉS =====================

    // Férié fixe (MM-DD)
    const isFixedHoliday = (date) => {
        if (!date) return false;
        const month = (date.getMonth() + 1).toString().padStart(2, "0");
        const day = date.getDate().toString().padStart(2, "0");
        const dateStr = `${month}-${day}`;
        return fixedHolidays.some((h) => h.date === dateStr && h.selected);
    };

    // ✅ Férié Hijri (multi-jours)
    const isHijriHoliday = (date) => {
        if (!date) return false;

        return hijriHolidays.some((h) => {
            if (!h.selected || !h.date) return false;

            const holidayDate = new Date(h.date);
            const checkDate = new Date(date);
            holidayDate.setHours(0, 0, 0, 0);
            checkDate.setHours(0, 0, 0, 0);

            for (let i = 0; i < (h.duration || 1); i++) {
                const testDate = new Date(holidayDate);
                testDate.setDate(testDate.getDate() + i);
                testDate.setHours(0, 0, 0, 0);

                if (testDate.getTime() === checkDate.getTime()) return true;
            }
            return false;
        });
    };

    const isNonWorkingDay = (date) => {
        if (!date) return false;
        let dayIndex = date.getDay();
        dayIndex = dayIndex === 0 ? 6 : dayIndex - 1;
        const dayName = WEEKDAYS[dayIndex];
        return !workDays[dayName].isWorking;
    };

    const getCongeInfo = (date) => {
        if (!date) return null;
        const y = date.getFullYear();
        const m = (date.getMonth() + 1).toString().padStart(2, "0");
        const d = date.getDate().toString().padStart(2, "0");
        const dateStr = `${y}-${m}-${d}`;

        return historiqueDemandes.find((demande) => {
            if (demande.statut === "refuse" || demande.statut === "annule") return false;
            const debutDate = new Date(demande.dateDebut);
            const finDate = new Date(demande.dateFin);
            const checkDate = new Date(dateStr);
            debutDate.setHours(0, 0, 0, 0);
            finDate.setHours(0, 0, 0, 0);
            checkDate.setHours(0, 0, 0, 0);
            return checkDate >= debutDate && checkDate <= finDate;
        });
    };

    // ✅ Nom du jour avec TOUS les types de fériés
    const getHolidayName = (date) => {
        if (!date) return "";
        const names = [];

        // Férié fixe
        const month = (date.getMonth() + 1).toString().padStart(2, "0");
        const day = date.getDate().toString().padStart(2, "0");
        const dateStr = `${month}-${day}`;
        const fixedHoliday = fixedHolidays.find((h) => h.date === dateStr && h.selected);
        if (fixedHoliday) names.push(`🎉 ${fixedHoliday.description}`);

        // ✅ Férié Hijri (avec indicateur de jour)
        const year = date.getFullYear();
        const m2 = (date.getMonth() + 1).toString().padStart(2, "0");
        const d2 = date.getDate().toString().padStart(2, "0");
        const fullDate = `${year}-${m2}-${d2}`;

        hijriHolidays.forEach((h) => {
            if (!h.selected || !h.date) return;

            const holidayDate = new Date(h.date);
            holidayDate.setHours(0, 0, 0, 0);

            for (let i = 0; i < (h.duration || 1); i++) {
                const test = new Date(holidayDate);
                test.setDate(test.getDate() + i);
                test.setHours(0, 0, 0, 0);

                const testStr = `${test.getFullYear()}-${(test.getMonth() + 1).toString().padStart(2, "0")}-${test.getDate().toString().padStart(2, "0")}`;

                if (testStr === fullDate) {
                    const dayLabel = h.duration > 1 ? ` (Jour ${i + 1}/${h.duration})` : "";
                    names.push(`🌙 ${h.name}${dayLabel}`);
                    break;
                }
            }
        });

        // Congé personnel
        const congeInfo = getCongeInfo(date);
        if (congeInfo) {
            let statutLabel = " (Approuvé)";
            if (congeInfo.statut === "enAttente") statutLabel = " (En attente)";
            if (congeInfo.statut === "reporte") statutLabel = " (Reporté)";
            names.push(`📅 ${congeInfo.typeConge}${statutLabel}`);
        }

        return names.join(" • ");
    };

    const getMonthData = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);

        let startOffset = firstDay.getDay() - 1;
        if (startOffset === -1) startOffset = 6;

        const daysInMonth = lastDay.getDate();
        const weeks = [];
        let currentWeek = [];

        for (let i = 0; i < startOffset; i++) currentWeek.push(null);

        for (let day = 1; day <= daysInMonth; day++) {
            if (currentWeek.length === 7) {
                weeks.push(currentWeek);
                currentWeek = [];
            }
            currentWeek.push(new Date(year, month, day));
        }

        while (currentWeek.length < 7) currentWeek.push(null);
        weeks.push(currentWeek);
        return weeks;
    };

    // ✅ Classes CSS améliorées (priorités: congé > hijri > fixe > weekend)
    const getDayClass = (date) => {
        if (!date) return "dcDay--empty";

        // Priorité 1: Congés personnels
        const congeInfo = getCongeInfo(date);
        if (congeInfo) {
            if (congeInfo.statut === "enAttente") return "dcDay--pending";
            if (congeInfo.statut === "approuve" || congeInfo.statut === "reporte") return "dcDay--approved";
        }

        // Priorité 2: Fériés Hijri
        if (isHijriHoliday(date)) return "dcDay--hijri";

        // Priorité 3: Fériés fixes
        if (isFixedHoliday(date)) return "dcDay--holiday";

        // Priorité 4: Weekend
        if (isNonWorkingDay(date)) return "dcDay--weekend";

        return "dcDay--normal";
    };

    // ===================== BADGE =====================
    const getStatutBadge = (statut) => {
        const config = {
            enAttente: { label: "En attente", bg: "#fff7db", color: "#8a6a00", icon: Clock },
            approuve: { label: "Approuvé", bg: "#e9f8ef", color: "#0f6b3f", icon: CheckCircle },
            refuse: { label: "Refusé", bg: "#ffe7ea", color: "#8b1d2b", icon: XCircle },
            annule: { label: "Annulé", bg: "#f3f4f6", color: "#6b7280", icon: XCircle },
            reporte: { label: "Reporté", bg: "#e0f2fe", color: "#0369a1", icon: RefreshCw },
            reporte_en_attente: { label: "Report en attente", bg: "#f0f9ff", color: "#0c4a6e", icon: RefreshCw },
        };

        const { label, bg, color, icon: Icon } = config[statut] || config.enAttente;
        return (
            <span className="dcBadge" style={{ backgroundColor: bg, color }}>
                <Icon size={14} />
                {label}
            </span>
        );
    };

    // ===================== COUNTS =====================
    const pendingCount = historiqueDemandes.filter((d) => d.statut === "enAttente" || d.statut === "reporte_en_attente").length;

    const approvedCount = historiqueDemandes.filter((d) => d.statut === "approuve").length;
    const reportedCount = historiqueDemandes.filter((d) => d.statut === "reporte").length;
    const refusedCount = historiqueDemandes.filter((d) => d.statut === "refuse").length;
    const cancelledCount = historiqueDemandes.filter((d) => d.statut === "annule").length;


    // ===================== SUBMIT (API) =====================
    const handleSubmitDemande = (e) => {
        e.preventDefault();
        setSoldeError("");

        if (!formData.absenceTypeId) {
            setSoldeError("Veuillez sélectionner un type de congé.");
            return;
        }
        if (!formData.dateDebut || !formData.dateFin) {
            setSoldeError("Veuillez renseigner la date de début et la date de fin.");
            return;
        }

        const selectedTypeLabel = getAbsenceTypeLabel(typesAbsences.find(t => String(t.id) === String(formData.absenceTypeId)));
        const duree = calculerDuree(formData.dateDebut, formData.dateFin);

        // ✅ VÉRIFICATION DU MAX DE JOURS PAR PRISE
        if (selectedTypeObj?.maxDaysPerRequest && duree > selectedTypeObj.maxDaysPerRequest) {
            setSoldeError(
                `La durée choisie (${duree} jour${duree > 1 ? "s" : ""}) dépasse le maximum autorisé pour ce type de congé (${selectedTypeObj.maxDaysPerRequest} jour${selectedTypeObj.maxDaysPerRequest > 1 ? "s" : ""}).`
            );
            return;
        }

        // ✅ VÉRIFICATION PÉRIODE DE RÉFÉRENCE
        if (selectedTypeObj?.periodeDe && formData.dateDebut && new Date(formData.dateDebut) < new Date(selectedTypeObj.periodeDe)) {
            setSoldeError(`Ce type de congé ne peut pas être pris avant le ${formatDate(selectedTypeObj.periodeDe)} (période de référence).`);
            return;
        }
        if (selectedTypeObj?.periodeA && formData.dateFin && new Date(formData.dateFin) > new Date(selectedTypeObj.periodeA)) {
            setSoldeError(`Ce type de congé ne peut pas être pris après le ${formatDate(selectedTypeObj.periodeA)} (période de référence).`);
            return;
        }

        // ✅ JUSTIFICATIF & AUTORISATION
        if (selectedTypeObj?.requireJustificatif && selectedTypeObj?.justificatifMandatory && !formData.justificatif) {
            setSoldeError(`Le document "${selectedTypeObj.justificationType}" est obligatoire pour ce type de congé.`);
            return;
        }
        if (selectedTypeObj?.requireAutorisation && !formData.autorisation) {
            setSoldeError("L'autorisation est obligatoire pour ce type de congé.");
            return;
        }

        const payload = {
            absenceTypeId: Number(formData.absenceTypeId),
            dateDebut: formData.dateDebut,
            dateFin: formData.dateFin,
            commentaire: formData.commentaire || "",
            requestType: "conge",
            justificatif: formData.justificatif,
            autorisation: formData.autorisation,
            demiJourneeDebut: !!formData.demiJourneeDebut,
            demiJourneeDebutType: formData.demiJourneeDebutType || null,
            demiJourneeFin: !!formData.demiJourneeFin,
            demiJourneeFinType: formData.demiJourneeFinType || null,
        };

        const executeSubmission = () => {
            dispatch(createLeaveRequestRequest(payload));
            setFormData({
                absenceTypeId: "",
                dateDebut: "",
                dateFin: "",
                commentaire: "",
                justificatif: null,
                autorisation: null,
                demiJourneeDebut: false,
                demiJourneeDebutType: "MATIN",
                demiJourneeFin: false,
                demiJourneeFinType: "MATIN",
            });
        };

        // ✅ VÉRIFICATION DU SOLDE (AVEC OPTION NÉGATIF)
        const typeCongeObj = soldesConges.find((s) => s.type === selectedTypeLabel);
        if (typeCongeObj && typeCongeObj.restant < duree) {
            if (selectedTypeObj?.negativeBalanceAllowed) {
                const futureBalance = typeCongeObj.restant - duree;
                
                // Si un seuil maximum est défini
                if (selectedTypeObj.maxNegativeBalance !== null && 
                    selectedTypeObj.maxNegativeBalance !== undefined && 
                    selectedTypeObj.maxNegativeBalance > 0 &&
                    Math.abs(futureBalance) > selectedTypeObj.maxNegativeBalance) {
                    setSoldeError(
                        `Le solde négatif maximum autorisé (${selectedTypeObj.maxNegativeBalance} jours) sera dépassé. Votre solde actuel est de ${typeCongeObj.restant} jours.`
                    );
                    return;
                }

                // ✅ Avertissement via SweetAlert (ne bloque PAS la soumission si confirmé)
                Swal.fire({
                    title: '⚠️ Solde insuffisant',
                    html: `
                        <div style="text-align: left; font-size: 14px; line-height: 1.6;">
                            <p>Votre solde actuel de <strong>${typeCongeObj.type}</strong> est de <strong>${typeCongeObj.restant} jour${typeCongeObj.restant > 1 ? "s" : ""}</strong>.</p>
                            <p>Vous demandez <strong>${duree} jour${duree > 1 ? "s" : ""}</strong>.</p>
                            <hr style="margin: 10px 0; border-color: #eee;" />
                            <p style="color: #dc2626; font-weight: 600;">
                                Votre solde après cette demande sera de <strong>${futureBalance} jour${Math.abs(futureBalance) > 1 ? "s" : ""}</strong>.
                            </p>
                        </div>
                    `,
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: '#204385',
                    cancelButtonColor: '#ef4444',
                    confirmButtonText: 'Oui, je confirme',
                    cancelButtonText: 'Annuler',
                    reverseButtons: true,
                    customClass: {
                        popup: 'swal-negative-balance'
                    }
                }).then((result) => {
                    if (result.isConfirmed) {
                        executeSubmission();
                    }
                });
                return;
            } else {
                // ❌ Solde négatif NON autorisé → bloquer
                setSoldeError(
                    `La période choisie (${duree} jour${duree > 1 ? "s" : ""}) dépasse votre solde disponible de ${typeCongeObj.type} (${typeCongeObj.restant} jour${typeCongeObj.restant > 1 ? "s" : ""}).`
                );
                return;
            }
        }

        executeSubmission();
    };

    // ===================== JUSTIFICATIF (UI ONLY) =====================
    const handleUploadJustificatif = (e) => {
        e.preventDefault();
        setShowJustificatifModal(false);
        setJustificatifUpload({ demandeId: null, fichier: null });
    };

    // ===================== ANNULER (API) =====================
    const handleAnnulerDemande = () => {
        if (!selectedDemande) return;

        dispatch(cancelLeaveRequestRequest(selectedDemande.id));

        setShowAnnulationModal(false);
        setSelectedDemande(null);
    };

    // ===================== TABS =====================
    const tabs = [
        { id: "solde", label: "Mon solde de congés", icon: Calendar },
        { id: "historique", label: "Mes demandes", icon: FileText },
        { id: "agenda", label: "Agenda", icon: Calendar },
        { id: "demande", label: "Nouvelle demande", icon: Plus },
    ];

    return (
        <div className="dcPage">
            <div className="dcWrap">
                {/* Header */}
                <div className="dcHeader dcMax">
                    <h1>Mes congés et absences</h1>
                    <p>Soumettez une nouvelle demande et suivez son avancement.</p>
                </div>

                <div className="dcCard dcMax">
                    {/* Tabs */}
                    <div className="dcTabs">
                        {tabs.map((tab) => {
                            const Icon = tab.icon;
                            return (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`dcTab ${activeTab === tab.id ? "is-active" : ""}`}
                                    type="button"
                                >
                                    <Icon size={18} />
                                    {tab.label}
                                </button>
                            );
                        })}
                    </div>

                    <div className="dcBody">
                        {/* API error */}
                        {apiError && (
                            <div className="dcAlert" style={{ marginBottom: 14 }}>
                                <AlertCircle size={18} color="#9f1239" style={{ marginTop: 1 }} />
                                <div>
                                    <h4>Erreur</h4>
                                    <p>{apiError}</p>
                                </div>
                            </div>
                        )}

                        {/* ===== SOLDE ===== */}
                        {activeTab === "solde" && (
                            <div>
                                <div>
                                    <h2 className="dcH2">Mon solde de congés</h2>
                                    <p className="dcSub">Vue d'ensemble de vos congés disponibles</p>
                                </div>

                                <div className="dcKpiGrid">
                                    <div className="dcBigKpi">
                                        <div className="dcBigKpiTop">
                                            <span className="dcBigLabel">
                                                {soldePrincipal ? soldePrincipal.type : "Solde disponible"}
                                            </span>
                                            <span className="dcBigMeta">{new Date().getFullYear()}</span>
                                        </div>
                                        <div className="dcBigValue">
                                            <span className="dcBigNumber">
                                                {soldePrincipal ? soldePrincipal.restant : 0}
                                            </span>
                                            <span className="dcBigUnit">jours</span>
                                        </div>
                                    </div>

                                    <div className="dcKpi" style={{ borderTop: "4px solid #f59e0b", background: "#fffbeb" }}>
                                        <div className="dcKpiTop">
                                            <Clock size={18} color="#f59e0b" />
                                            <span className="dcKpiNum" style={{ color: "#b45309" }}>{pendingCount}</span>
                                        </div>
                                        <div className="dcKpiTitle" style={{ color: "#b45309" }}>En attente</div>
                                        <div className="dcKpiSub" style={{ color: "#d97706" }}>
                                            {historiqueDemandes.filter((d) => d.statut === "enAttente").reduce((acc, d) => acc + (d.duree || 0), 0)} jours
                                        </div>
                                    </div>

                                    <div className="dcKpi" style={{ borderTop: "4px solid #10b981", background: "#f0fdf4" }}>
                                        <div className="dcKpiTop">
                                            <CheckCircle size={18} color="#10b981" />
                                            <span className="dcKpiNum" style={{ color: "#047857" }}>{approvedCount}</span>
                                        </div>
                                        <div className="dcKpiTitle" style={{ color: "#047857" }}>Approuvées</div>
                                        <div className="dcKpiSub" style={{ color: "#059669" }}>
                                            {historiqueDemandes.filter((d) => d.statut === "approuve").reduce((acc, d) => acc + (d.duree || 0), 0)} jours
                                        </div>
                                    </div>

                                    <div className="dcKpi" style={{ borderTop: "4px solid #ef4444", background: "#fef2f2" }}>
                                        <div className="dcKpiTop">
                                            <XCircle size={18} color="#ef4444" />
                                            <span className="dcKpiNum" style={{ color: "#b91c1c" }}>{refusedCount}</span>
                                        </div>
                                        <div className="dcKpiTitle" style={{ color: "#b91c1c" }}>Refusées</div>
                                        <div className="dcKpiSub" style={{ color: "#dc2626" }}>
                                            {historiqueDemandes.filter((d) => d.statut === "refuse").reduce((acc, d) => acc + (d.duree || 0), 0)} jours
                                        </div>
                                    </div>

                                    <div className="dcKpi" style={{ borderTop: "4px solid #6b7280", background: "#f9fafb" }}>
                                        <div className="dcKpiTop">
                                            <XCircle size={18} color="#6b7280" />
                                            <span className="dcKpiNum" style={{ color: "#374151" }}>{cancelledCount}</span>
                                        </div>
                                        <div className="dcKpiTitle" style={{ color: "#374151" }}>Annulées</div>
                                        <div className="dcKpiSub" style={{ color: "#4b5563" }}>
                                            {historiqueDemandes.filter((d) => d.statut === "annule").reduce((acc, d) => acc + (d.duree || 0), 0)} jours
                                        </div>
                                    </div>

                                    <div className="dcKpi" style={{ borderTop: "4px solid #3b82f6", background: "#eff6ff" }}>
                                        <div className="dcKpiTop">
                                            <RefreshCw size={18} color="#3b82f6" />
                                            <span className="dcKpiNum" style={{ color: "#1d4ed8" }}>{reportedCount}</span>
                                        </div>
                                        <div className="dcKpiTitle" style={{ color: "#1d4ed8" }}>Reportées</div>
                                        <div className="dcKpiSub" style={{ color: "#2563eb" }}>
                                            {historiqueDemandes.filter((d) => d.statut === "reporte").reduce((acc, d) => acc + (d.duree || 0), 0)} jours
                                        </div>
                                    </div>

                                </div>

                                {/* Délégation Card */}
                                {/*<div className="dcBox" style={{ marginTop: 24, borderLeft: "4px solid #3b82f6", background: "#f0f7ff" }}>*/}
                                {/*    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>*/}
                                {/*        <div>*/}
                                {/*            <h3 className="dcBoxTitle" style={{ marginBottom: 4 }}>*/}
                                {/*                <ShieldCheck size={20} color="#3b82f6" />*/}
                                {/*                Délégation de saisie RH*/}
                                {/*            </h3>*/}
                                {/*            <p style={{ fontSize: "13px", color: "#64748b", margin: 0 }}>*/}
                                {/*                Autoriser l'administration RH à saisir des demandes de congés en votre nom.*/}
                                {/*            </p>*/}
                                {/*        </div>*/}
                                {/*        <div className="dcToggle">*/}
                                {/*            <input */}
                                {/*                type="checkbox" */}
                                {/*                id="delegationToggle" */}
                                {/*                checked={isDelegationEnabled}*/}
                                {/*                onChange={(e) => handleToggleDelegation(e.target.checked)}*/}
                                {/*            />*/}
                                {/*            <label htmlFor="delegationToggle"></label>*/}
                                {/*        </div>*/}
                                {/*    </div>*/}
                                {/*</div>*/}


                                <div className="dcBox" style={{ marginTop: 16 }}>
                                    <h3 className="dcBoxTitle">
                                        <CheckCircle size={18} color="#16a34a" />
                                        États de mes congés
                                    </h3>

                                    {loading || balanceLoading ? (
                                        <div className="dcEmpty">
                                            <Clock size={32} />
                                            <div>Chargement...</div>
                                        </div>
                                    ) : combinedHistory.length === 0 ? (
                                        <div className="dcEmpty">
                                            <CheckCircle size={32} />
                                            <div>Aucun mouvement trouvé</div>
                                        </div>
                                    ) : (
                                        <div className="dcTableWrap">
                                            <table className="dcTable">
                                                <thead>
                                                <tr>
                                                    <th>Référence</th>
                                                    <th>Date de l'opération</th>
                                                    <th>Motif</th>
                                                    <th>Type de congé</th>
                                                    <th>Solde Initial</th>
                                                    <th>Solde débit</th>
                                                    <th>Solde crédit</th>
                                                    <th>Solde Restant</th>
                                                </tr>
                                                </thead>
                                                <tbody>
                                                {combinedHistory.map((item, index) => {
                                                    const isPositive = item.valeur > 0;
                                                    const isNegative = item.valeur < 0;
                                                    const isCancelledOrRefused = item.isLeaveRequest && (item.statut === "annule" || item.statut === "refuse");
                                                    
                                                    const statusBadge = item.isLeaveRequest ? {
                                                        label: item.statut === "enAttente" ? "En attente" :
                                                               item.statut === "approuve" ? "Approuvé" :
                                                               item.statut === "refuse" ? "Refusé" :
                                                               item.statut === "annule" ? "Annulé" :
                                                               item.statut === "reporte" ? "Reporté" : item.statut,
                                                        bg: item.statut === "enAttente" ? "#fef3c7" :
                                                            item.statut === "approuve" ? "#dcfce7" :
                                                            item.statut === "refuse" ? "#fee2e2" :
                                                            item.statut === "annule" ? "#f3f4f6" :
                                                            item.statut === "reporte" ? "#dbeafe" : "#f3f4f6",
                                                        color: item.statut === "enAttente" ? "#d97706" :
                                                               item.statut === "approuve" ? "#16a34a" :
                                                               item.statut === "refuse" ? "#ef4444" :
                                                               item.statut === "annule" ? "#6b7280" :
                                                               item.statut === "reporte" ? "#2563eb" : "#6b7280",
                                                    } : null;

                                                    return (
                                                        <tr key={item.id || index} style={{
                                                            backgroundColor: isCancelledOrRefused ? "#f9fafb" : isPositive ? "#f0fdf4" : isNegative ? "#fef2f2" : "#fff",
                                                            borderLeft: isCancelledOrRefused ? "3px solid #d1d5db" : isPositive ? "3px solid #16a34a" : isNegative ? "3px solid #ef4444" : "3px solid #6b7280",
                                                            opacity: isCancelledOrRefused ? 0.55 : 1
                                                        }}>
                                                            <td>
                                                                <div style={{fontWeight: 700, color: "#15803d", textDecoration: isCancelledOrRefused ? "line-through" : "none"}}>{item.employeeName || "—"}</div>
                                                                <div style={{fontSize: "11px", color: "#6b7280"}}>{item.employeeCode || "—"}</div>
                                                            </td>
                                                            <td>{formatDate(item.dateMouvement)}</td>
                                                            <td style={{fontSize: "12px", color: "#4b5563", maxWidth: "220px"}}>
                                                                <div style={{fontWeight: "bold", color: "#111827", display: "flex", alignItems: "center", flexWrap: "wrap", gap: "6px"}}>
                                                                    <span style={{textDecoration: isCancelledOrRefused ? "line-through" : "none"}}>{item.reference}</span>
                                                                    {statusBadge && (
                                                                        <span style={{
                                                                            fontSize: "10px",
                                                                            padding: "1px 6px",
                                                                            borderRadius: "4px",
                                                                            fontWeight: 600,
                                                                            display: "inline-block",
                                                                            backgroundColor: statusBadge.bg,
                                                                            color: statusBadge.color,
                                                                        }}>
                                                                            {statusBadge.label}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <div>{item.motif || item.commentaire || "—"}</div>
                                                            </td>
                                                            <td>
                                                                <div className="dcTypeCell">
                                                                    <span className="dcDot" style={{backgroundColor: item.absenceTypeCouleur || (isPositive ? "#16a34a" : "#ef4444")}}/>
                                                                    <span>{item.absenceTypeNom || item.typeMouvement}</span>
                                                                </div>
                                                            </td>
                                                            <td style={{textAlign: "center", fontWeight: 600}}>
                                                                {item.soldeInitialSnapshot !== null && item.soldeInitialSnapshot !== undefined 
                                                                    ? item.soldeInitialSnapshot + "j" 
                                                                    : "—"}
                                                            </td>
                                                            <td style={{textAlign: "center", color: "#ef4444", fontWeight: 700, textDecoration: isCancelledOrRefused ? "line-through" : "none"}}>
                                                                {isNegative ? item.valeur + "j" : "—"}
                                                            </td>
                                                            <td style={{ textAlign: "center", fontWeight: 700, color: "#16a34a" }}>
                                                                {isPositive ? "+" + item.valeur + "j" : "—"}
                                                            </td>
                                                            <td style={{ textAlign: "center", fontWeight: 800, background: "#f8fafc" }}>
                                                                {item.soldeRestantSnapshot !== null && item.soldeRestantSnapshot !== undefined ? item.soldeRestantSnapshot + "j" : "—"}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* ===== NOUVELLE DEMANDE ===== */}
                        {activeTab === "demande" && (
                            <div>
                                <h2 className="dcH2">Formulaire de demande de congé</h2>

                                {(paramLoading || balanceLoading || typesCongesDisponibles.length === 0) && (
                                    <div className="dcInfo" style={{marginTop: 10}}>
                                        <Clock size={16}/>
                                        Chargement des types de congés et soldes...
                                    </div>
                                )}

                                {soldeError && (
                                    <div className="dcAlert">
                                        <AlertCircle size={18} color="#9f1239" style={{marginTop: 1}}/>
                                        <div>
                                            <h4>Erreur</h4>
                                            <p>{soldeError}</p>
                                        </div>
                                    </div>
                                )}

                                <form className="dcForm" onSubmit={handleSubmitDemande}>
                                    <div className="dcField">
                                        <label>
                                            Type de congé <span className="dcReq">*</span>
                                        </label>

                                        <select
                                            className="dcSelect"
                                            value={formData.absenceTypeId}
                                            onChange={(e) => {
                                                setFormData({
                                                    ...formData,
                                                    absenceTypeId: e.target.value,
                                                    justificatif: null,
                                                    autorisation: null,
                                                });
                                                setSoldeError("");
                                            }}
                                            required
                                            disabled={loading || paramLoading || balanceLoading || typesCongesDisponibles.length === 0}
                                        >
                                            <option value="">Sélectionner un type</option>

                                            {typesCongesDisponibles.map((type) => {
                                                return (
                                                    <option key={type.id} value={type.id}>
                                                        {type.label}
                                                    </option>
                                                );
                                            })}
                                        </select>
                                    </div>

                                    <div className="dcGrid2">
                                        <div className="dcField">
                                            <label>
                                                Date de début <span className="dcReq">*</span>
                                            </label>
                                            <input
                                                className="dcInput"
                                                type="date"
                                                value={formData.dateDebut}
                                                onChange={(e) => setFormData({...formData, dateDebut: e.target.value})}
                                                required
                                                disabled={loading}
                                            />
                                        </div>

                                        <div className="dcField">
                                            <label>
                                                Date de fin <span className="dcReq">*</span>
                                            </label>
                                            <input
                                                className="dcInput"
                                                type="date"
                                                value={formData.dateFin}
                                                onChange={(e) => setFormData({ ...formData, dateFin: e.target.value })}
                                                required
                                                disabled={loading}
                                            />
                                        </div>
                                    </div>

                                    {/* Options demi-journée */}
                                    {formData.dateDebut && formData.dateFin && (
                                        <div style={{
                                            margin: "12px 0",
                                            padding: "12px",
                                            backgroundColor: "#f9fafb",
                                            borderRadius: "8px",
                                            border: "1px solid #e5e7eb",
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: "10px"
                                        }}>
                                            {formData.dateDebut === formData.dateFin ? (
                                                <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                                                    <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer", fontWeight: "500", fontSize: "13px", color: "#374151" }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={!!formData.demiJourneeDebut}
                                                            onChange={(e) => setFormData({
                                                                ...formData,
                                                                demiJourneeDebut: e.target.checked,
                                                                demiJourneeFin: e.target.checked
                                                            })}
                                                        />
                                                        <span>Demander une demi-journée</span>
                                                    </label>
                                                    {formData.demiJourneeDebut && (
                                                        <select
                                                            className="dcSelect"
                                                            style={{ width: "auto", padding: "4px 8px", fontSize: "12px", minWidth: "120px" }}
                                                            value={formData.demiJourneeDebutType}
                                                            onChange={(e) => setFormData({
                                                                ...formData,
                                                                demiJourneeDebutType: e.target.value,
                                                                demiJourneeFinType: e.target.value
                                                            })}
                                                        >
                                                            <option value="MATIN">Matin</option>
                                                            <option value="APRES_MIDI">Après-midi</option>
                                                        </select>
                                                    )}
                                                </div>
                                            ) : (
                                                <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                                                    <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                                                        <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer", fontWeight: "500", fontSize: "13px", color: "#374151" }}>
                                                            <input
                                                                type="checkbox"
                                                                checked={!!formData.demiJourneeDebut}
                                                                onChange={(e) => setFormData({
                                                                    ...formData,
                                                                    demiJourneeDebut: e.target.checked
                                                                })}
                                                            />
                                                            <span>Le premier jour est une demi-journée</span>
                                                        </label>
                                                        {formData.demiJourneeDebut && (
                                                            <select
                                                                className="dcSelect"
                                                                style={{ width: "auto", padding: "4px 8px", fontSize: "12px", minWidth: "120px" }}
                                                                value={formData.demiJourneeDebutType}
                                                                onChange={(e) => setFormData({
                                                                    ...formData,
                                                                    demiJourneeDebutType: e.target.value
                                                                })}
                                                            >
                                                                <option value="MATIN">Matin</option>
                                                                <option value="APRES_MIDI">Après-midi</option>
                                                            </select>
                                                        )}
                                                    </div>
                                                    <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                                                        <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer", fontWeight: "500", fontSize: "13px", color: "#374151" }}>
                                                            <input
                                                                type="checkbox"
                                                                checked={!!formData.demiJourneeFin}
                                                                onChange={(e) => setFormData({
                                                                    ...formData,
                                                                    demiJourneeFin: e.target.checked
                                                                })}
                                                            />
                                                            <span>Le dernier jour est une demi-journée</span>
                                                        </label>
                                                        {formData.demiJourneeFin && (
                                                            <select
                                                                className="dcSelect"
                                                                style={{ width: "auto", padding: "4px 8px", fontSize: "12px", minWidth: "120px" }}
                                                                value={formData.demiJourneeFinType}
                                                                onChange={(e) => setFormData({
                                                                    ...formData,
                                                                    demiJourneeFinType: e.target.value
                                                                })}
                                                            >
                                                                <option value="MATIN">Matin</option>
                                                                <option value="APRES_MIDI">Après-midi</option>
                                                            </select>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {formData.dateDebut && formData.dateFin && (
                                        <div className="dcInfo">
                                            <Clock size={16} />
                                            Durée : {calculerDuree(formData.dateDebut, formData.dateFin)} jour(s)
                                        </div>
                                    )}

                                    {selectedTypeObj && (selectedTypeObj.periodeDe || selectedTypeObj.periodeA) && (
                                        <div className="dcInfo dcInfo--warn" style={{ marginBottom: 15 }}>
                                            <Calendar size={16} />
                                            <span>
                                                Période de validité : <strong>{selectedTypeObj.periodeDe ? `Du ${formatDate(selectedTypeObj.periodeDe)}` : ""} {selectedTypeObj.periodeA ? `au ${formatDate(selectedTypeObj.periodeA)}` : ""}</strong>
                                            </span>
                                        </div>
                                    )}

                                    <div className="dcField">
                                        <label>Commentaire / Motif</label>
                                        <textarea
                                            className="dcTextarea"
                                            value={formData.commentaire}
                                            onChange={(e) => setFormData({ ...formData, commentaire: e.target.value })}
                                            placeholder="Précisez le motif..."
                                            disabled={loading}
                                        />
                                    </div>

                                    {selectedTypeObj?.requireJustificatif && (
                                        <div className="dcField">
                                            <label>
                                                {selectedTypeObj.justificationType} {selectedTypeObj.justificatifMandatory && <span className="dcReq">*</span>}
                                            </label>

                                            <div className="dcUploadBox">
                                                <Upload size={30} color="#9ca3af" />
                                                <div className="dcUploadHint">Glissez-déposez ou cliquez</div>

                                                <input
                                                    type="file"
                                                    className="dcHiddenFile"
                                                    id="dc-justif"
                                                    accept=".pdf,.jpg,.jpeg,.png"
                                                    onChange={(e) => setFormData({ ...formData, justificatif: e.target.files?.[0] || null })}
                                                    disabled={loading}
                                                />
                                                <label
                                                    htmlFor="dc-justif"
                                                    className="dcBtn dcBtn--primary"
                                                    style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
                                                >
                                                    <Upload size={16} />
                                                    Parcourir
                                                </label>

                                                {formData.justificatif && <div className="dcUploadOk">✓ {formData.justificatif.name}</div>}
                                            </div>

                                            <div className="dcHelp">PDF, JPG, PNG (max 5Mo)</div>
                                        </div>
                                    )}

                                    {selectedTypeObj?.requireAutorisation && (
                                        <div className="dcField">
                                            <label>
                                                Autorisation <span className="dcReq">*</span>
                                            </label>

                                            <div className="dcUploadBox dcUploadBox--accent">
                                                <FileTextIcon size={30} color="#ee645f" />
                                                <div className="dcUploadHint">Document d'autorisation requis (RH, manager...)</div>

                                                <input
                                                    type="file"
                                                    className="dcHiddenFile"
                                                    id="dc-auto"
                                                    accept=".pdf,.jpg,.jpeg,.png"
                                                    onChange={(e) => setFormData({ ...formData, autorisation: e.target.files?.[0] || null })}
                                                    disabled={loading}
                                                />
                                                <label
                                                    htmlFor="dc-auto"
                                                    className="dcBtn dcBtn--primary"
                                                    style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
                                                >
                                                    <Upload size={16} />
                                                    Parcourir
                                                </label>

                                                {formData.autorisation && <div className="dcUploadOk">✓ {formData.autorisation.name}</div>}
                                            </div>

                                            <div className="dcHelp">PDF, JPG, PNG (max 5Mo)</div>
                                        </div>
                                    )}

                                    <div className="dcFormFooter">
                                        <button
                                            type="button"
                                            className="dcBtn dcBtn--ghost"
                                            onClick={() => {
                                                setFormData({
                                                    absenceTypeId: "",
                                                    dateDebut: "",
                                                    dateFin: "",
                                                    commentaire: "",
                                                    justificatif: null,
                                                    autorisation: null,
                                                });
                                                setSoldeError("");
                                            }}
                                            disabled={loading}
                                        >
                                            Réinitialiser
                                        </button>

                                        <button
                                            type="submit"
                                            className="dcBtn dcBtn--primary"
                                            disabled={loading || paramLoading || balanceLoading || !formData.absenceTypeId}
                                        >
                                            {loading ? "Envoi..." : "Soumettre la demande"}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        )}

                        {/* ===== HISTORIQUE ===== */}
                        {activeTab === "historique" && (
                            <div>
                                <h2 className="dcH2" style={{ marginBottom: 12 }}>
                                    Historique des demandes
                                </h2>

                                <div className="dcTableWrap">
                                    <table className="dcTable">
                                        <thead>
                                        <tr>
                                            <th>Référence</th>
                                            <th>Type</th>
                                            <th>Période</th>

                                            <th>Durée</th>
                                            <th>Date de la demande</th>
                                            <th>Statut</th>
                                            <th>Justificatif</th>
                                            <th>Autorisation</th>
                                            <th>Actions</th>
                                        </tr>
                                        </thead>

                                        <tbody>
                                        {loading ? (
                                            <tr>
                                                <td colSpan={8} style={{ padding: 16 }}>
                                                    <div className="dcEmpty" style={{ minHeight: 140 }}>
                                                        <Clock size={28} />
                                                        <div>Chargement...</div>
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : historiqueDemandes.length === 0 ? (
                                            <tr>
                                                <td colSpan={8} style={{ padding: 16 }}>
                                                    <div className="dcEmpty" style={{ minHeight: 140 }}>
                                                        <FileText size={28} />
                                                        <div>Aucune demande trouvée</div>
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : (
                                            historiqueDemandes.map((demande) => {
                                                const typeConf = (typesCongesDisponibles || []).find((t) => t.label === demande.typeConge);

                                                return (
                                                    <tr key={demande.id}>
                                                        <td>
                                                            <div className="dcSmall" style={{ fontWeight: 600 }}>{demande.employeeName || demande.demandeurName}</div>
                                                            <div className="dcTiny" style={{ color: "#6b7280" }}>{demande.employeeCode || demande.demandeurCode}</div>
                                                            {demande.demandeurId !== demande.employeeId && (
                                                                <div className="dcTiny" style={{ fontStyle: "italic", fontSize: "10px" }}>Par: {demande.demandeurName}</div>
                                                            )}
                                                        </td>


                                                        <td>
                                                            <div className="dcTypeCell">
                                                                <span className="dcDot" style={{ backgroundColor: demande.couleurType }} />
                                                                <span style={{ fontWeight: 600, color: "#204385" }}>{demande.typeConge}</span>
                                                            </div>
                                                        </td>

                                                        <td>
                                                            <div>
                                                                <div className="dcSmall">{formatDate(demande.dateDebut)}</div>
                                                                {demande.dateDebut !== demande.dateFin && <div className="dcTiny">au {formatDate(demande.dateFin)}</div>}
                                                            </div>
                                                        </td>

                                                        <td style={{ fontWeight: 700, color: "#204385" }}>{demande.duree}j</td>
                                                        <td className="dcSmall">{formatDate(demande.datesDemande)}</td>
                                                        <td>{getStatutBadge(demande.statut)}</td>

                                                        <td className="dcSmall">
                                                            {demande.justificatifFourni ? (
                                                                <span style={{ color: "#16a34a", fontWeight: 700 }}>
                                                                    <CheckCircle size={14} /> {demande.justificatifNom}
                                                                </span>
                                                            ) : typeConf?.requireJustificatif ? (
                                                                <span style={{ color: "#f59e0b", fontWeight: 700 }}>Requis</span>
                                                            ) : (
                                                                <span style={{ color: "#9ca3af" }}>—</span>
                                                            )}
                                                        </td>

                                                        <td className="dcSmall">
                                                            {demande.autorisationFournie ? (
                                                                <span style={{ color: "#16a34a", fontWeight: 700 }}>
                                                                    <CheckCircle size={14} /> {demande.autorisationNom}
                                                                </span>
                                                            ) : typeConf?.requireAutorisation ? (
                                                                <span style={{ color: "#f59e0b", fontWeight: 700 }}>Requis</span>
                                                            ) : (
                                                                <span style={{ color: "#9ca3af" }}>—</span>
                                                            )}
                                                        </td>

                                                        <td>
                                                            <div className="dcActions">
                                                                {demande.statut === "refuse" && demande.motifRejet && (
                                                                    <button
                                                                        className="dcIconBtn"
                                                                        title="Voir motif"
                                                                        type="button"
                                                                        onClick={() => setSelectedDemande(demande)}
                                                                    >
                                                                        <Eye size={16} />
                                                                    </button>
                                                                )}

                                                                {demande.statut === "enAttente" && (
                                                                    <button
                                                                        className="dcIconBtn is-danger"
                                                                        title="Annuler"
                                                                        type="button"
                                                                        onClick={() => {
                                                                            setSelectedDemande(demande);
                                                                            setShowAnnulationModal(true);
                                                                        }}
                                                                    >
                                                                        <Trash2 size={16} />
                                                                    </button>
                                                                )}

                                                                {demande.statut === "approuve" && (
                                                                    <>
                                                                        <button
                                                                            className="dcIconBtn is-danger"
                                                                            title="Annuler"
                                                                            type="button"
                                                                            onClick={() => {
                                                                                setSelectedDemande(demande);
                                                                                setShowAnnulationModal(true);
                                                                            }}
                                                                        >
                                                                            <XCircle size={16} />
                                                                        </button>
                                                                        <button
                                                                            className="dcIconBtn is-primary"
                                                                            title="Reporter"
                                                                            type="button"
                                                                            onClick={() => {
                                                                                setSelectedDemande(demande);
                                                                                setShowReportModal(true);
                                                                            }}
                                                                        >
                                                                            <RefreshCw size={16} />
                                                                        </button>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* ===== ✅ CALENDRIER AMÉLIORÉ ===== */}
                        {activeTab === "agenda" && (
                            <div className="dcAgenda">
                                <div className="dcAgenda__intro">
                                    <div>
                                        <h2 className="dcH2">Mon agenda de congés</h2>
                                        <p className="dcSub">Retrouvez toutes vos demandes, avec leur statut à chaque date.</p>
                                    </div>
                                    <div className="dcAgenda__nav" aria-label="Navigation du calendrier">
                                        <button type="button" className="dcAgenda__navButton" onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))} title="Mois précédent">
                                            <ChevronLeft size={18} />
                                        </button>
                                        <button type="button" className="dcAgenda__today" onClick={() => setCurrentMonth(new Date())}>Aujourd’hui</button>
                                        <button type="button" className="dcAgenda__navButton" onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))} title="Mois suivant">
                                            <ChevronRight size={18} />
                                        </button>
                                    </div>
                                </div>

                                <div className="dcAgenda__month">{formatMonth(currentMonth)}</div>
                                <div className="dcAgenda__calendar">
                                    <div className="dcAgenda__weekdays">
                                        {["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"].map((day) => <div key={day}>{day}</div>)}
                                    </div>
                                    <div className="dcAgenda__grid">
                                        {getMonthData(currentMonth).flat().map((date, index) => {
                                            const dateKey = date && `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
                                            const leavesForDay = date ? historiqueDemandes.filter((demande) => {
                                                const start = String(demande.dateDebut || "").slice(0, 10);
                                                const end = String(demande.dateFin || "").slice(0, 10);
                                                return start && end && dateKey >= start && dateKey <= end;
                                            }) : [];
                                            const isToday = date && date.toDateString() === new Date().toDateString();

                                            return (
                                                <div key={dateKey || `empty-${index}`} className={`dcAgenda__day ${!date ? "is-empty" : ""} ${isToday ? "is-today" : ""}`}>
                                                    {date && <>
                                                        <span className="dcAgenda__date">{date.getDate()}</span>
                                                        <div className="dcAgenda__events">
                                                            {leavesForDay.map((demande) => {
                                                                const status = {
                                                                    enAttente: { label: "En attente", className: "is-pending", icon: Clock },
                                                                    approuve: { label: "Approuvé", className: "is-approved", icon: CheckCircle },
                                                                    refuse: { label: "Refusé", className: "is-refused", icon: XCircle },
                                                                    annule: { label: "Annulé", className: "is-cancelled", icon: XCircle },
                                                                    reporte: { label: "Reporté", className: "is-postponed", icon: RefreshCw },
                                                                    reporte_en_attente: { label: "Report en attente", className: "is-pending", icon: RefreshCw },
                                                                }[demande.statut] || { label: "En attente", className: "is-pending", icon: Clock };
                                                                const StatusIcon = status.icon;
                                                                return <div key={demande.id} className={`dcAgenda__event ${status.className}`} title={`${demande.typeConge} — ${status.label}`}>
                                                                    <StatusIcon size={12} />
                                                                    <span>{demande.typeConge}</span>
                                                                </div>;
                                                            })}
                                                        </div>
                                                    </>}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                <div className="dcAgenda__legend">
                                    <span className="dcAgenda__legendTitle">Statuts</span>
                                    <span className="dcAgenda__legendItem is-pending"><Clock size={13} /> En attente</span>
                                    <span className="dcAgenda__legendItem is-approved"><CheckCircle size={13} /> Approuvé</span>
                                    <span className="dcAgenda__legendItem is-refused"><XCircle size={13} /> Refusé</span>
                                    <span className="dcAgenda__legendItem is-cancelled"><XCircle size={13} /> Annulé</span>
                                    <span className="dcAgenda__legendItem is-postponed"><RefreshCw size={13} /> Reporté</span>
                                </div>
                            </div>
                        )}

                        {/*{activeTab === "calendrier" && (*/}
                        {/*    <div>*/}
                        {/*        <div className="dcCalTop">*/}
                        {/*            <div className="dcCalBox">*/}
                        {/*                <h4>Prochains congés</h4>*/}
                        {/*                {historiqueDemandes*/}
                        {/*                    .filter((d) => d.statut !== "refuse" && d.statut !== "annule" && new Date(d.dateDebut) >= new Date())*/}
                        {/*                    .slice(0, 3)*/}
                        {/*                    .map((demande) => (*/}
                        {/*                        <div key={demande.id} className="dcCalItem">*/}
                        {/*                            <span className="dcDot" style={{ backgroundColor: demande.couleurType, marginTop: 6 }} />*/}
                        {/*                            <div>*/}
                        {/*                                <div style={{ fontSize: 13, fontWeight: 700, color: "#204385" }}>{demande.typeConge}</div>*/}
                        {/*                                <div className="dcTiny">*/}
                        {/*                                    {formatDate(demande.dateDebut)} → {formatDate(demande.dateFin)} ({demande.duree}j)*/}
                        {/*                                </div>*/}
                        {/*                                <div style={{ marginTop: 4 }}>{getStatutBadge(demande.statut)}</div>*/}
                        {/*                            </div>*/}
                        {/*                        </div>*/}
                        {/*                    ))}*/}
                        {/*            </div>*/}

                        {/*            <div className="dcCalBox">*/}
                        {/*                <h4>Prochains jours fériés</h4>*/}
                        {/*                {fixedHolidays.slice(0, 3).map((h, i) => (*/}
                        {/*                    <div key={i} className="dcCalItem">*/}
                        {/*                        <span className="dcDot" style={{ backgroundColor: "#ef4444", marginTop: 6 }} />*/}
                        {/*                        <div>*/}
                        {/*                            <div style={{ fontSize: 13, fontWeight: 700, color: "#204385" }}>{h.description}</div>*/}
                        {/*                            <div className="dcTiny">{h.date}</div>*/}
                        {/*                        </div>*/}
                        {/*                    </div>*/}
                        {/*                ))}*/}
                        {/*            </div>*/}
                        {/*        </div>*/}

                        {/*        <div className="dcMonthCard">*/}
                        {/*            <div className="dcMonthNav">*/}
                        {/*                <button*/}
                        {/*                    className="dcMonthBtn"*/}
                        {/*                    type="button"*/}
                        {/*                    onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}*/}
                        {/*                >*/}
                        {/*                    ‹*/}
                        {/*                </button>*/}

                        {/*                <div className="dcMonthTitle">{formatMonth(currentMonth)}</div>*/}

                        {/*                <button*/}
                        {/*                    className="dcMonthBtn"*/}
                        {/*                    type="button"*/}
                        {/*                    onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}*/}
                        {/*                >*/}
                        {/*                    ›*/}
                        {/*                </button>*/}
                        {/*            </div>*/}

                        {/*            <table className="dcCalTable">*/}
                        {/*                <thead>*/}
                        {/*                <tr>*/}
                        {/*                    {WEEKDAYS_SHORT.map((d) => (*/}
                        {/*                        <th key={d}>{d}</th>*/}
                        {/*                    ))}*/}
                        {/*                </tr>*/}
                        {/*                </thead>*/}

                        {/*                <tbody>*/}
                        {/*                {getMonthData(currentMonth).map((week, i) => (*/}
                        {/*                    <tr key={i}>*/}
                        {/*                        {week.map((date, j) => {*/}
                        {/*                            const tip = date ? getHolidayName(date) : "";*/}
                        {/*                            const cellClass = date ? getDayClass(date) : "dcDay--empty";*/}
                        {/*                            const hasTip = !!tip;*/}

                        {/*                            return (*/}
                        {/*                                <td key={j} className={`${cellClass} ${hasTip ? "dcHasTip" : ""}`}>*/}
                        {/*                                    {date && (*/}
                        {/*                                        <>*/}
                        {/*                                            <div className="dcDayNum">{date.getDate()}</div>*/}
                        {/*                                            {tip && <div className="dcTooltip">{tip}</div>}*/}
                        {/*                                        </>*/}
                        {/*                                    )}*/}
                        {/*                                </td>*/}
                        {/*                            );*/}
                        {/*                        })}*/}
                        {/*                    </tr>*/}
                        {/*                ))}*/}
                        {/*                </tbody>*/}
                        {/*            </table>*/}

                        {/*            /!* ✅ Légende enrichie *!/*/}
                        {/*            <div className="dcLegend">*/}
                        {/*                <div className="dcLegendItem">*/}
                        {/*                    <span className="dcLegendBox" style={{ background: "#e5e7eb" }} />*/}
                        {/*                    Weekend*/}
                        {/*                </div>*/}
                        {/*                <div className="dcLegendItem">*/}
                        {/*                    <span className="dcLegendBox" style={{ background: "#fee2e2", borderColor: "#fecaca" }} />*/}
                        {/*                    Férié fixe*/}
                        {/*                </div>*/}
                        {/*                <div className="dcLegendItem">*/}
                        {/*                    <span className="dcLegendBox" style={{ background: "linear-gradient(135deg, #a855f7 0%, #9333ea 100%)", borderColor: "#7e22ce" }} />*/}
                        {/*                    Férié Hijri*/}
                        {/*                </div>*/}
                        {/*                <div className="dcLegendItem">*/}
                        {/*                    <span className="dcLegendBox" style={{ background: "#dbeafe", borderColor: "#60a5fa" }} />*/}
                        {/*                    Approuvé*/}
                        {/*                </div>*/}
                        {/*                <div className="dcLegendItem">*/}
                        {/*                    <span className="dcLegendBox" style={{ background: "#fef3c7", borderColor: "#fbbf24" }} />*/}
                        {/*                    En attente*/}
                        {/*                </div>*/}
                        {/*            </div>*/}
                        {/*        </div>*/}
                        {/*    </div>*/}
                        {/*)}*/}
                    </div>
                </div>

                {/* ===== MODALS (inchangés) ===== */}
                {showAnnulationModal && selectedDemande && (
                    <div className="dcModal">
                        <div className="dcModalPanel">
                            <div className="dcModalHeader">
                                <div>
                                    <h3 className="dcModalTitle">Annuler la demande</h3>
                                    <div className="dcModalSub">Votre solde sera restauré</div>
                                </div>
                                <button
                                    className="dcXBtn"
                                    type="button"
                                    onClick={() => {
                                        setShowAnnulationModal(false);
                                        setSelectedDemande(null);
                                    }}
                                >
                                    <X size={18} />
                                </button>
                            </div>

                            <div className="dcModalBody">
                                <div className="dcBox" style={{ boxShadow: "none" }}>
                                    <div className="dcTypeCell" style={{ marginBottom: 6 }}>
                                        <span className="dcDot" style={{ backgroundColor: selectedDemande.couleurType }} />
                                        <span style={{ fontWeight: 800, color: "#204385" }}>{selectedDemande.typeConge}</span>
                                    </div>
                                    <div className="dcSmall">
                                        Du {formatDate(selectedDemande.dateDebut)} au {formatDate(selectedDemande.dateFin)}
                                    </div>
                                    <div className="dcSmall">Durée : {selectedDemande.duree} jour(s)</div>
                                </div>

                                <div className="dcWarnBox" style={{ marginTop: 12 }}>
                                    <strong>Note :</strong> Le solde sera automatiquement restauré après annulation.
                                </div>
                            </div>

                            <div className="dcModalFooter">
                                <button
                                    className="dcBtn dcBtn--ghost"
                                    type="button"
                                    onClick={() => {
                                        setShowAnnulationModal(false);
                                        setSelectedDemande(null);
                                    }}
                                >
                                    Conserver
                                </button>
                                <button className="dcBtn dcBtn--danger" type="button" onClick={handleAnnulerDemande}>
                                    Confirmer l'annulation
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {showJustificatifModal && (
                    <div className="dcModal">
                        <div className="dcModalPanel dcModalPanel--sm">
                            <div className="dcModalHeader">
                                <div>
                                    <h3 className="dcModalTitle">Ajouter un justificatif</h3>
                                    <div className="dcModalSub">Téléversement du document</div>
                                </div>
                                <button
                                    className="dcXBtn"
                                    type="button"
                                    onClick={() => {
                                        setShowJustificatifModal(false);
                                        setJustificatifUpload({ demandeId: null, fichier: null });
                                    }}
                                >
                                    <X size={18} />
                                </button>
                            </div>

                            <form onSubmit={handleUploadJustificatif}>
                                <div className="dcModalBody">
                                    <div className="dcUploadBox">
                                        <Upload size={34} color="#9ca3af" />
                                        <div className="dcUploadHint">Glissez-déposez ou cliquez</div>

                                        <input
                                            type="file"
                                            id="dc-modal-justif"
                                            accept=".pdf,.jpg,.jpeg,.png"
                                            onChange={(e) => setJustificatifUpload({ ...justificatifUpload, fichier: e.target.files?.[0] || null })}
                                            required
                                        />

                                        <label htmlFor="dc-modal-justif" className="dcBtn dcBtn--primary" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                                            <Upload size={16} />
                                            Parcourir
                                        </label>

                                        {justificatifUpload.fichier && <div className="dcUploadOk">✓ {justificatifUpload.fichier.name}</div>}
                                    </div>

                                    <div className="dcHelp">PDF, JPG, PNG (max 5Mo)</div>
                                </div>

                                <div className="dcModalFooter">
                                    <button
                                        type="button"
                                        className="dcBtn dcBtn--ghost"
                                        onClick={() => {
                                            setShowJustificatifModal(false);
                                            setJustificatifUpload({ demandeId: null, fichier: null });
                                        }}
                                    >
                                        Annuler
                                    </button>
                                    <button type="submit" className="dcBtn dcBtn--primary" disabled={!justificatifUpload.fichier}>
                                        Télécharger
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {selectedDemande &&
                    !showAnnulationModal &&
                    activeTab === "historique" &&
                    selectedDemande.statut === "refuse" &&
                    selectedDemande.motifRejet && (
                        <div className="dcModal">
                            <div className="dcModalPanel dcModalPanel--sm">
                                <div className="dcModalHeader">
                                    <div>
                                        <h3 className="dcModalTitle">Motif de rejet</h3>
                                        <div className="dcModalSub">Détails</div>
                                    </div>
                                    <button className="dcXBtn" type="button" onClick={() => setSelectedDemande(null)}>
                                        <X size={18} />
                                    </button>
                                </div>

                                <div className="dcModalBody">
                                    <div className="dcBox" style={{ boxShadow: "none" }}>
                                        <div className="dcTypeCell" style={{ marginBottom: 6 }}>
                                            <span className="dcDot" style={{ backgroundColor: selectedDemande.couleurType }} />
                                            <span style={{ fontWeight: 800, color: "#204385" }}>{selectedDemande.typeConge}</span>
                                        </div>
                                        <div className="dcSmall">
                                            Du {formatDate(selectedDemande.dateDebut)} au {formatDate(selectedDemande.dateFin)}
                                        </div>
                                        <div className="dcWarnBox" style={{ marginTop: 12 }}>
                                            {selectedDemande.motifRejet}
                                        </div>
                                    </div>
                                </div>

                                <div className="dcModalFooter">
                                    <button className="dcBtn dcBtn--primary" type="button" onClick={() => setSelectedDemande(null)}>
                                        Fermer
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
            </div>
            {/* MODALE DE REPORT (RE-PLANIFIER) */}
            {showReportModal && selectedDemande && (
                <ReportModal
                    demande={selectedDemande}
                    onClose={() => {
                        setShowReportModal(false);
                        setSelectedDemande(null);
                    }}
                    onConfirm={(dates) => {
                        dispatch(postponeLeaveRequestRequest({
                            id: selectedDemande.id,
                            startDate: dates.startDate,
                            endDate: dates.endDate
                        }));
                        setShowReportModal(false);
                        setSelectedDemande(null);
                    }}
                />
            )}
        </div>
    );
};

// ===================== ✅ COMPOSANTS INTERNES =====================

const ReportModal = ({ demande, onClose, onConfirm }) => {
    const [newStartDate, setNewStartDate] = useState("");
    const [newEndDate, setNewEndDate] = useState("");
    const [error, setError] = useState("");

    const oldDuration = useMemo(() => {
        if (!demande.dateDebut || !demande.dateFin) return 0;
        const d1 = new Date(demande.dateDebut);
        const d2 = new Date(demande.dateFin);
        return Math.ceil((d2 - d1) / (1000 * 60 * 60 * 24)) + 1;
    }, [demande]);

    const currentDuration = useMemo(() => {
        if (!newStartDate || !newEndDate) return 0;
        const d1 = new Date(newStartDate);
        const d2 = new Date(newEndDate);
        if (isNaN(d1.getTime()) || isNaN(d2.getTime())) return 0;
        
        // Calcul identique au backend (différence de jours + 1)
        const diffTime = d2.getTime() - d1.getTime();
        const diffDays = Math.round(diffTime / (1000 * 3600 * 24));
        return diffDays + 1;
    }, [newStartDate, newEndDate]);

    // Aide pour l'utilisateur : ajuster automatiquement la date de fin
    const autoAdjustEndDate = () => {
        if (!newStartDate || oldDuration <= 0) return;
        const d1 = new Date(newStartDate);
        const d2 = new Date(d1);
        d2.setDate(d1.getDate() + oldDuration - 1);
        setNewEndDate(d2.toISOString().split("T")[0]);
        setError("");
    };


    const handleConfirm = () => {
        setError("");
        
        if (!newStartDate || !newEndDate) {
            setError("Veuillez sélectionner les deux dates.");
            return;
        }

        const d1 = new Date(newStartDate);
        const d2 = new Date(newEndDate);
        const oldD1 = new Date(demande.dateDebut);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (d1 < today) {
            setError("La nouvelle date de début ne peut pas être dans le passé.");
            return;
        }

        if (d1 <= oldD1) {
            setError(`La nouvelle date de début doit être strictement postérieure à l'ancienne (${oldD1.toLocaleDateString()}).`);
            return;
        }

        if (d2 < d1) {
            setError("La date de fin ne peut pas être antérieure à la date de début.");
            return;
        }

        if (currentDuration !== oldDuration) {
            setError(`Attention : La durée doit être de ${oldDuration} jours. Vous avez sélectionné une période de ${currentDuration} jours.`);
            return;
        }

        onConfirm({ startDate: newStartDate, endDate: newEndDate });

    };


    return (
        <div className="dcModal">
            <div className="dcModalPanel dcModalPanel--sm">
                <div className="dcModalHeader">
                    <div>
                        <div className="dcModalTitle">
                            <RefreshCw size={16} style={{ marginRight: 8, verticalAlign: "middle" }} />
                            Reporter la demande
                        </div>
                        <div className="dcModalSub">Changez les dates pour la même durée ({oldDuration} jours)</div>
                    </div>
                    <button className="dcXBtn" type="button" onClick={onClose}><X size={18} /></button>
                </div>

                <div className="dcModalBody">
                    <div className="dcWarnBox" style={{ marginBottom: 20 }}>
                        Ancienne période : <b>{new Date(demande.dateDebut).toLocaleDateString()}</b> au <b>{new Date(demande.dateFin).toLocaleDateString()}</b>
                    </div>

                    <div className="dcGrid2">
                        <div className="dcField">
                            <label>Nouvelle date de début</label>
                            <input 
                                type="date" 
                                value={newStartDate} 
                                onChange={(e) => {
                                    setNewStartDate(e.target.value);
                                    setError("");
                                }}

                                className="dcInput"
                                style={{ width: "100%" }}
                            />
                        </div>
                        <div className="dcField">
                            <label>Nouvelle date de fin</label>
                            <div style={{ display: "flex", gap: 8 }}>
                                <input 
                                    type="date" 
                                    value={newEndDate} 
                                    onChange={(e) => {
                                        setNewEndDate(e.target.value);
                                        setError("");
                                    }}

                                    className="dcInput"
                                    style={{ flex: 1 }}
                                />
                                <button 
                                    type="button" 
                                    className="dcIconBtn" 
                                    title="Ajuster selon la durée originale"
                                    onClick={autoAdjustEndDate}
                                    style={{ background: "#f0f9ff", border: "1px solid #bae6fd" }}
                                >
                                    <TrendingUp size={16} color="#0369a1" />
                                </button>
                            </div>
                        </div>
                    </div>


                    {error && (
                        <div className="dcAlert" style={{ marginTop: 15 }}>
                            <AlertCircle size={16} color="#ef4444" />
                            <div style={{ color: "#9f1239", fontSize: "12px" }}>{error}</div>
                        </div>
                    )}
                    
                    <div className="dcInfo" style={{ marginTop: 20, backgroundColor: currentDuration > 0 && currentDuration !== oldDuration ? "#fff1f2" : "#eff6ff", borderColor: currentDuration > 0 && currentDuration !== oldDuration ? "#fecdd3" : "#dbeafe" }}>
                        <Clock size={16} color={currentDuration > 0 && currentDuration !== oldDuration ? "#ef4444" : "#3b82f6"} />
                        <span style={{ color: currentDuration > 0 && currentDuration !== oldDuration ? "#9f1239" : "inherit" }}>
                            Durée sélectionnée : <b>{currentDuration} jours</b> 
                            {currentDuration > 0 && currentDuration !== oldDuration && ` (Doit être ${oldDuration})`}
                        </span>
                    </div>

                </div>

                <div className="dcModalFooter">
                    <button className="dcBtn dcBtn--ghost" type="button" onClick={onClose}>Annuler</button>
                    <button 
                        className="dcBtn dcBtn--primary" 
                        type="button"
                        onClick={handleConfirm}
                        disabled={currentDuration !== oldDuration}
                    >
                        Confirmer le report
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DemandeConges;
